/**
 * InventoryPage - Enterprise Inventory Management
 * 
 * Features:
 * - Multi-warehouse inventory management
 * - Serial/matricola tracking
 * - Status management with color coding
 * - Movement history with filters
 * - Warehouse CRUD management
 * - Excel Import/Export
 * - Contract integration
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import api from '../lib/api';
import ExcelInventoryManager from '../components/ExcelInventoryManager';
import {
  Package,
  Search,
  Plus,
  Edit2,
  Warehouse,
  BarChart3,
  History,
  Filter,
  RefreshCw,
  Loader2,
  MapPin,
  Tag,
  Calendar,
  FileText,
  ArrowRightLeft,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  Truck,
  Wrench,
  RotateCcw,
  Ban,
  HelpCircle,
  ChevronRight,
  MoreHorizontal,
  Building2,
  Box,
  ClipboardList,
  FileSpreadsheet,
  Trash2,
  Settings,
  Archive,
  ArrowUpDown,
  User
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

// Status configuration
const STATUS_CONFIG = {
  disponibile: { label: 'Disponibile', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  riservato: { label: 'Riservato', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
  assegnato: { label: 'Assegnato', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: ClipboardList },
  in_uscita: { label: 'In Uscita', color: 'bg-purple-100 text-purple-700 border-purple-200', icon: Truck },
  installato: { label: 'Installato', color: 'bg-cyan-100 text-cyan-700 border-cyan-200', icon: Wrench },
  difettoso: { label: 'Difettoso', color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle },
  reso: { label: 'Reso', color: 'bg-gray-100 text-gray-700 border-gray-200', icon: RotateCcw },
  in_verifica: { label: 'In Verifica', color: 'bg-orange-100 text-orange-700 border-orange-200', icon: HelpCircle },
  non_disponibile: { label: 'Non Disponibile', color: 'bg-slate-200 text-slate-700 border-slate-300', icon: Ban }
};

// Movement type configuration
const MOVEMENT_TYPE_CONFIG = {
  ingresso: { label: 'Ingresso', color: 'bg-emerald-100 text-emerald-700' },
  trasferimento: { label: 'Trasferimento', color: 'bg-blue-100 text-blue-700' },
  riserva: { label: 'Riserva', color: 'bg-amber-100 text-amber-700' },
  assegnazione: { label: 'Assegnazione', color: 'bg-indigo-100 text-indigo-700' },
  uscita: { label: 'Uscita', color: 'bg-purple-100 text-purple-700' },
  installazione: { label: 'Installazione', color: 'bg-cyan-100 text-cyan-700' },
  reso: { label: 'Reso', color: 'bg-gray-100 text-gray-700' },
  difetto: { label: 'Difetto', color: 'bg-red-100 text-red-700' },
  correzione_manuale: { label: 'Correzione', color: 'bg-slate-100 text-slate-700' },
  annulla_riserva: { label: 'Annulla Riserva', color: 'bg-orange-100 text-orange-700' },
  annulla_assegnazione: { label: 'Annulla Assegn.', color: 'bg-rose-100 text-rose-700' }
};

// Warehouse type configuration
const WAREHOUSE_TYPE_CONFIG = {
  centrale: { label: 'Centrale', color: 'bg-blue-100 text-blue-700' },
  tecnici: { label: 'Tecnici', color: 'bg-emerald-100 text-emerald-700' },
  cantiere: { label: 'Cantiere', color: 'bg-orange-100 text-orange-700' },
  resi: { label: 'Resi', color: 'bg-amber-100 text-amber-700' },
  difettosi: { label: 'Difettosi', color: 'bg-red-100 text-red-700' },
  transit: { label: 'Transito', color: 'bg-purple-100 text-purple-700' }
};

export default function InventoryPage() {
  // Active tab
  const [activeTab, setActiveTab] = useState('articoli');
  
  // State - Items
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedWarehouse, setSelectedWarehouse] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  
  // State - Movements
  const [movements, setMovements] = useState([]);
  const [movementsLoading, setMovementsLoading] = useState(false);
  const [movementFilter, setMovementFilter] = useState({ type: 'all', page: 1 });
  const [movementsTotal, setMovementsTotal] = useState(0);
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showWarehouseDialog, setShowWarehouseDialog] = useState(false);
  const [showDeleteWarehouseDialog, setShowDeleteWarehouseDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedWarehouseItem, setSelectedWarehouseItem] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state for new item
  const [newItem, setNewItem] = useState({
    catalog_item_id: '',
    serial_number: '',
    lotto: '',
    warehouse_id: '',
    ubicazione: '',
    scaffale: '',
    stato: 'disponibile',
    data_ingresso: new Date().toISOString().split('T')[0],
    documento_acquisto: '',
    garanzia_scadenza: '',
    note: ''
  });
  
  // Form state for warehouse
  const [warehouseForm, setWarehouseForm] = useState({
    codice_magazzino: '',
    nome_magazzino: '',
    tipo_magazzino: 'centrale',
    indirizzo: '',
    responsabile: '',
    note: ''
  });
  
  // Catalog items for selection
  const [catalogItems, setCatalogItems] = useState([]);
  const [catalogSearch, setCatalogSearch] = useState('');

  // Load data
  const loadWarehouses = useCallback(async (includeInactive = false) => {
    try {
      const response = await api.get(`/inventory/warehouses?include_inactive=${includeInactive}`);
      setWarehouses(response.data);
    } catch (error) {
      console.error('Error loading warehouses:', error);
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const response = await api.get('/inventory/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('limit', 30);
      
      if (selectedWarehouse !== 'all') {
        params.append('warehouse_id', selectedWarehouse);
      }
      if (selectedStatus !== 'all') {
        params.append('stato', selectedStatus);
      }
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      const response = await api.get(`/inventory/items?${params.toString()}`);
      setItems(response.data.items || []);
      setTotalPages(response.data.pages || 1);
      setTotalItems(response.data.total || 0);
    } catch (error) {
      console.error('Error loading items:', error);
      toast.error('Errore nel caricamento inventario');
    } finally {
      setLoading(false);
    }
  }, [currentPage, selectedWarehouse, selectedStatus, searchTerm]);

  const loadMovements = useCallback(async () => {
    setMovementsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', movementFilter.page);
      params.append('limit', 50);
      
      if (movementFilter.type !== 'all') {
        params.append('movimento_tipo', movementFilter.type);
      }
      
      const response = await api.get(`/inventory/movements?${params.toString()}`);
      setMovements(response.data.movements || []);
      setMovementsTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error loading movements:', error);
      toast.error('Errore nel caricamento movimenti');
    } finally {
      setMovementsLoading(false);
    }
  }, [movementFilter]);

  const loadCatalogItems = useCallback(async (search = '') => {
    try {
      const response = await api.get(`/catalog/items?search=${search}&limit=50&active_only=true`);
      setCatalogItems(response.data.items || []);
    } catch (error) {
      console.error('Error loading catalog:', error);
    }
  }, []);

  useEffect(() => {
    loadWarehouses(true);
    loadStats();
  }, [loadWarehouses, loadStats]);

  useEffect(() => {
    if (activeTab === 'articoli') {
      loadItems();
    }
  }, [activeTab, loadItems]);

  useEffect(() => {
    if (activeTab === 'movimenti') {
      loadMovements();
    }
  }, [activeTab, loadMovements]);

  useEffect(() => {
    if (showAddDialog) {
      loadCatalogItems();
    }
  }, [showAddDialog, loadCatalogItems]);

  // Create new item
  const handleCreateItem = async () => {
    if (!newItem.catalog_item_id || !newItem.serial_number || !newItem.warehouse_id) {
      toast.error('Compila i campi obbligatori');
      return;
    }
    
    setSaving(true);
    try {
      await api.post('/inventory/items', newItem);
      toast.success('Articolo aggiunto all\'inventario');
      setShowAddDialog(false);
      setNewItem({
        catalog_item_id: '',
        serial_number: '',
        lotto: '',
        warehouse_id: '',
        ubicazione: '',
        scaffale: '',
        stato: 'disponibile',
        data_ingresso: new Date().toISOString().split('T')[0],
        documento_acquisto: '',
        garanzia_scadenza: '',
        note: ''
      });
      loadItems();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setSaving(false);
    }
  };

  // View item detail
  const handleViewDetail = async (item) => {
    try {
      const response = await api.get(`/inventory/items/${item.id}`);
      setSelectedItem(response.data);
      setShowDetailDialog(true);
    } catch (error) {
      toast.error('Errore nel caricamento dettagli');
    }
  };

  // Transfer item
  const handleTransfer = async (newWarehouseId, ubicazione, scaffale, note) => {
    if (!selectedItem || !newWarehouseId) return;
    
    setSaving(true);
    try {
      await api.post(`/inventory/items/${selectedItem.id}/transfer`, {
        nuovo_warehouse_id: newWarehouseId,
        nuova_ubicazione: ubicazione,
        nuovo_scaffale: scaffale,
        note: note
      });
      toast.success('Articolo trasferito');
      setShowTransferDialog(false);
      loadItems();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel trasferimento');
    } finally {
      setSaving(false);
    }
  };

  // Change status
  const handleChangeStatus = async (newStatus, note) => {
    if (!selectedItem) return;
    
    setSaving(true);
    try {
      await api.post(`/inventory/items/${selectedItem.id}/change-status`, {
        nuovo_stato: newStatus,
        note: note
      });
      toast.success('Stato aggiornato');
      setShowStatusDialog(false);
      loadItems();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel cambio stato');
    } finally {
      setSaving(false);
    }
  };

  // Warehouse CRUD
  const handleSaveWarehouse = async () => {
    if (!warehouseForm.codice_magazzino || !warehouseForm.nome_magazzino) {
      toast.error('Compila codice e nome magazzino');
      return;
    }
    
    setSaving(true);
    try {
      if (selectedWarehouseItem) {
        // Update
        await api.put(`/inventory/warehouses/${selectedWarehouseItem.id}`, warehouseForm);
        toast.success('Magazzino aggiornato');
      } else {
        // Create
        await api.post('/inventory/warehouses', warehouseForm);
        toast.success('Magazzino creato');
      }
      setShowWarehouseDialog(false);
      setSelectedWarehouseItem(null);
      setWarehouseForm({
        codice_magazzino: '',
        nome_magazzino: '',
        tipo_magazzino: 'centrale',
        indirizzo: '',
        responsabile: '',
        note: ''
      });
      loadWarehouses(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteWarehouse = async () => {
    if (!selectedWarehouseItem) return;
    
    setSaving(true);
    try {
      await api.delete(`/inventory/warehouses/${selectedWarehouseItem.id}`);
      toast.success('Magazzino disattivato');
      setShowDeleteWarehouseDialog(false);
      setSelectedWarehouseItem(null);
      loadWarehouses(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella disattivazione');
    } finally {
      setSaving(false);
    }
  };

  const openEditWarehouse = (wh) => {
    setSelectedWarehouseItem(wh);
    setWarehouseForm({
      codice_magazzino: wh.codice_magazzino || '',
      nome_magazzino: wh.nome_magazzino || '',
      tipo_magazzino: wh.tipo_magazzino || 'centrale',
      indirizzo: wh.indirizzo || '',
      responsabile: wh.responsabile || '',
      note: wh.note || ''
    });
    setShowWarehouseDialog(true);
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.disponibile;
    const Icon = config.icon;
    return (
      <Badge variant="outline" className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  // Movement type badge
  const MovementTypeBadge = ({ type }) => {
    const config = MOVEMENT_TYPE_CONFIG[type] || { label: type, color: 'bg-gray-100 text-gray-700' };
    return (
      <Badge variant="outline" className={config.color}>
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Package className="h-6 w-6 text-primary" />
            Inventario
          </h1>
          <p className="text-muted-foreground">
            Gestione materiali fisici con matricole e seriali
          </p>
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-grid">
          <TabsTrigger value="articoli" className="flex items-center gap-2" data-testid="tab-articoli">
            <Package className="h-4 w-4" />
            <span className="hidden sm:inline">Articoli</span>
          </TabsTrigger>
          <TabsTrigger value="magazzini" className="flex items-center gap-2" data-testid="tab-magazzini">
            <Warehouse className="h-4 w-4" />
            <span className="hidden sm:inline">Magazzini</span>
          </TabsTrigger>
          <TabsTrigger value="movimenti" className="flex items-center gap-2" data-testid="tab-movimenti">
            <History className="h-4 w-4" />
            <span className="hidden sm:inline">Movimenti</span>
          </TabsTrigger>
          <TabsTrigger value="excel" className="flex items-center gap-2" data-testid="tab-excel">
            <FileSpreadsheet className="h-4 w-4" />
            <span className="hidden sm:inline">Excel</span>
          </TabsTrigger>
        </TabsList>

        {/* Tab: Articoli */}
        <TabsContent value="articoli" className="space-y-4">
          {/* Stats Dashboard */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 border-emerald-200">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-emerald-600 font-medium">Disponibili</p>
                      <p className="text-2xl font-bold text-emerald-700">{stats.by_status?.disponibile || 0}</p>
                    </div>
                    <CheckCircle2 className="h-8 w-8 text-emerald-500/50" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-amber-50 to-amber-100/50 border-amber-200">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-amber-600 font-medium">Riservati</p>
                      <p className="text-2xl font-bold text-amber-700">{stats.by_status?.riservato || 0}</p>
                    </div>
                    <Clock className="h-8 w-8 text-amber-500/50" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-200">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-blue-600 font-medium">Assegnati</p>
                      <p className="text-2xl font-bold text-blue-700">{stats.by_status?.assegnato || 0}</p>
                    </div>
                    <ClipboardList className="h-8 w-8 text-blue-500/50" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-cyan-50 to-cyan-100/50 border-cyan-200">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-cyan-600 font-medium">Installati</p>
                      <p className="text-2xl font-bold text-cyan-700">{stats.by_status?.installato || 0}</p>
                    </div>
                    <Wrench className="h-8 w-8 text-cyan-500/50" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-slate-50 to-slate-100/50">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-600 font-medium">Totale</p>
                      <p className="text-2xl font-bold">{stats.total || 0}</p>
                    </div>
                    <Box className="h-8 w-8 text-slate-400" />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Filters */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="flex-1 min-w-[200px]">
                  <Label className="text-xs text-muted-foreground">Cerca</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Seriale, lotto, marca, modello..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9"
                      data-testid="inventory-search"
                    />
                  </div>
                </div>
                
                <div className="w-48">
                  <Label className="text-xs text-muted-foreground">Magazzino</Label>
                  <Select value={selectedWarehouse} onValueChange={setSelectedWarehouse}>
                    <SelectTrigger data-testid="warehouse-filter">
                      <SelectValue placeholder="Tutti" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti i magazzini</SelectItem>
                      {warehouses.filter(w => w.attivo).map(wh => (
                        <SelectItem key={wh.id} value={wh.id}>
                          {wh.nome_magazzino}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="w-48">
                  <Label className="text-xs text-muted-foreground">Stato</Label>
                  <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                    <SelectTrigger data-testid="status-filter">
                      <SelectValue placeholder="Tutti" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti gli stati</SelectItem>
                      {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>
                          {config.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <Button variant="outline" onClick={() => { loadItems(); loadStats(); }}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
                
                <Button onClick={() => setShowAddDialog(true)} data-testid="add-inventory-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Ingresso
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Inventory Table */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">
                  Articoli in Inventario
                  <span className="ml-2 text-sm font-normal text-muted-foreground">
                    ({totalItems} totali)
                  </span>
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : items.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">Nessun articolo trovato</p>
                  <Button variant="outline" className="mt-4" onClick={() => setShowAddDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Aggiungi il primo articolo
                  </Button>
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Seriale</TableHead>
                        <TableHead>Articolo</TableHead>
                        <TableHead>Tipologia</TableHead>
                        <TableHead>Magazzino</TableHead>
                        <TableHead>Stato</TableHead>
                        <TableHead>Contratto</TableHead>
                        <TableHead className="text-right">Azioni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.map(item => (
                        <TableRow key={item.id} className="cursor-pointer hover:bg-muted/50">
                          <TableCell className="font-mono text-sm">
                            {item.serial_number}
                            {item.lotto && (
                              <span className="block text-xs text-muted-foreground">
                                Lotto: {item.lotto}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">{item.brand}</div>
                            <div className="text-sm text-muted-foreground">{item.model}</div>
                          </TableCell>
                          <TableCell className="text-sm">{item.type_code}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Warehouse className="h-3 w-3 text-muted-foreground" />
                              <span className="text-sm">{item.warehouse_name}</span>
                            </div>
                            {item.ubicazione && (
                              <div className="text-xs text-muted-foreground">{item.ubicazione}</div>
                            )}
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={item.stato} />
                          </TableCell>
                          <TableCell className="text-sm">
                            {item.contract_number || '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleViewDetail(item)}>
                                  <FileText className="h-4 w-4 mr-2" />
                                  Dettagli
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => { setSelectedItem(item); setShowTransferDialog(true); }}>
                                  <ArrowRightLeft className="h-4 w-4 mr-2" />
                                  Trasferisci
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => { setSelectedItem(item); setShowStatusDialog(true); }}>
                                  <RefreshCw className="h-4 w-4 mr-2" />
                                  Cambia stato
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-4">
                      <p className="text-sm text-muted-foreground">
                        Pagina {currentPage} di {totalPages}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                        >
                          Precedente
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                        >
                          Successiva
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Magazzini */}
        <TabsContent value="magazzini" className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold">Gestione Magazzini</h2>
              <p className="text-sm text-muted-foreground">Crea, modifica e gestisci i magazzini</p>
            </div>
            <Button onClick={() => { setSelectedWarehouseItem(null); setWarehouseForm({ codice_magazzino: '', nome_magazzino: '', tipo_magazzino: 'centrale', indirizzo: '', responsabile: '', note: '' }); setShowWarehouseDialog(true); }} data-testid="add-warehouse-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nuovo Magazzino
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {warehouses.map(wh => (
              <Card key={wh.id} className={`${!wh.attivo ? 'opacity-60 bg-muted/50' : ''}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Warehouse className="h-4 w-4" />
                        {wh.nome_magazzino}
                      </CardTitle>
                      <CardDescription className="font-mono">{wh.codice_magazzino}</CardDescription>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditWarehouse(wh)}>
                          <Edit2 className="h-4 w-4 mr-2" />
                          Modifica
                        </DropdownMenuItem>
                        {wh.attivo && (
                          <DropdownMenuItem 
                            className="text-red-600"
                            onClick={() => { setSelectedWarehouseItem(wh); setShowDeleteWarehouseDialog(true); }}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Disattiva
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Tipo:</span>
                      <Badge variant="outline" className={WAREHOUSE_TYPE_CONFIG[wh.tipo_magazzino]?.color || ''}>
                        {WAREHOUSE_TYPE_CONFIG[wh.tipo_magazzino]?.label || wh.tipo_magazzino}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Articoli:</span>
                      <span className="font-medium">{wh.item_count || 0}</span>
                    </div>
                    {wh.indirizzo && (
                      <div className="flex items-start gap-2 pt-2 border-t">
                        <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                        <span className="text-muted-foreground">{wh.indirizzo}</span>
                      </div>
                    )}
                    {!wh.attivo && (
                      <Badge variant="outline" className="bg-red-100 text-red-700 mt-2">
                        Disattivato
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Tab: Movimenti */}
        <TabsContent value="movimenti" className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold">Storico Movimenti</h2>
              <p className="text-sm text-muted-foreground">Tracciamento di tutti i movimenti inventario</p>
            </div>
            <div className="flex gap-2">
              <Select 
                value={movementFilter.type} 
                onValueChange={(v) => setMovementFilter({ ...movementFilter, type: v, page: 1 })}
              >
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Tipo movimento" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i tipi</SelectItem>
                  {Object.entries(MOVEMENT_TYPE_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={loadMovements}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <Card>
            <CardContent className="pt-4">
              {movementsLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : movements.length === 0 ? (
                <div className="text-center py-12">
                  <History className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">Nessun movimento trovato</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data/Ora</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Seriale</TableHead>
                      <TableHead>Stato</TableHead>
                      <TableHead>Magazzino</TableHead>
                      <TableHead>Utente</TableHead>
                      <TableHead>Note</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {movements.map(mov => (
                      <TableRow key={mov.id}>
                        <TableCell className="text-sm">
                          {new Date(mov.data_movimento).toLocaleString('it-IT', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </TableCell>
                        <TableCell>
                          <MovementTypeBadge type={mov.movimento_tipo} />
                        </TableCell>
                        <TableCell className="font-mono text-sm">{mov.serial_number}</TableCell>
                        <TableCell>
                          {mov.stato_precedente && mov.stato_nuovo ? (
                            <div className="flex items-center gap-1 text-sm">
                              <span className="text-muted-foreground">{STATUS_CONFIG[mov.stato_precedente]?.label || mov.stato_precedente}</span>
                              <ArrowRightLeft className="h-3 w-3" />
                              <span className="font-medium">{STATUS_CONFIG[mov.stato_nuovo]?.label || mov.stato_nuovo}</span>
                            </div>
                          ) : mov.stato_nuovo ? (
                            <span className="text-sm">{STATUS_CONFIG[mov.stato_nuovo]?.label || mov.stato_nuovo}</span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          {mov.warehouse_precedente && mov.warehouse_nuovo ? (
                            <span className="text-muted-foreground">Trasferimento</span>
                          ) : mov.warehouse_nuovo ? (
                            <span>→ {mov.warehouse_nuovo.substring(0, 8)}...</span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">{mov.user_name || '-'}</TableCell>
                        <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                          {mov.note || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              
              {movementsTotal > 50 && (
                <div className="flex justify-center mt-4">
                  <p className="text-sm text-muted-foreground">
                    Mostrati {movements.length} di {movementsTotal} movimenti
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Excel */}
        <TabsContent value="excel" className="space-y-4">
          <ExcelInventoryManager onImportComplete={() => { loadItems(); loadStats(); loadMovements(); }} />
        </TabsContent>
      </Tabs>

      {/* Add Item Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Ingresso Materiale</DialogTitle>
            <DialogDescription>
              Registra un nuovo articolo in inventario
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Catalog item selection */}
            <div>
              <Label>Articolo da Catalogo *</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Cerca nel catalogo..."
                  value={catalogSearch}
                  onChange={(e) => {
                    setCatalogSearch(e.target.value);
                    loadCatalogItems(e.target.value);
                  }}
                />
              </div>
              {catalogItems.length > 0 && (
                <ScrollArea className="h-40 border rounded-md mt-2">
                  {catalogItems.map(cat => (
                    <div
                      key={cat.id}
                      className={`p-2 cursor-pointer hover:bg-muted ${newItem.catalog_item_id === cat.id ? 'bg-primary/10' : ''}`}
                      onClick={() => setNewItem({ ...newItem, catalog_item_id: cat.id })}
                    >
                      <div className="font-medium text-sm">{cat.brand} {cat.model}</div>
                      <div className="text-xs text-muted-foreground">{cat.item_code} - {cat.type_code}</div>
                    </div>
                  ))}
                </ScrollArea>
              )}
              {newItem.catalog_item_id && (
                <p className="text-xs text-emerald-600 mt-1">
                  ✓ Articolo selezionato: {catalogItems.find(c => c.id === newItem.catalog_item_id)?.brand} {catalogItems.find(c => c.id === newItem.catalog_item_id)?.model}
                </p>
              )}
            </div>
            
            <Separator />
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Seriale / Matricola *</Label>
                <Input
                  value={newItem.serial_number}
                  onChange={(e) => setNewItem({ ...newItem, serial_number: e.target.value })}
                  placeholder="SN123456789"
                  data-testid="serial-input"
                />
              </div>
              <div>
                <Label>Lotto</Label>
                <Input
                  value={newItem.lotto}
                  onChange={(e) => setNewItem({ ...newItem, lotto: e.target.value })}
                  placeholder="LOT-2026-001"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Magazzino *</Label>
                <Select 
                  value={newItem.warehouse_id} 
                  onValueChange={(v) => setNewItem({ ...newItem, warehouse_id: v })}
                >
                  <SelectTrigger data-testid="warehouse-select">
                    <SelectValue placeholder="Seleziona magazzino" />
                  </SelectTrigger>
                  <SelectContent>
                    {warehouses.filter(w => w.attivo).map(wh => (
                      <SelectItem key={wh.id} value={wh.id}>
                        {wh.nome_magazzino}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Ubicazione</Label>
                <Input
                  value={newItem.ubicazione}
                  onChange={(e) => setNewItem({ ...newItem, ubicazione: e.target.value })}
                  placeholder="Scaffale A3"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data Ingresso</Label>
                <Input
                  type="date"
                  value={newItem.data_ingresso}
                  onChange={(e) => setNewItem({ ...newItem, data_ingresso: e.target.value })}
                />
              </div>
              <div>
                <Label>Scadenza Garanzia</Label>
                <Input
                  type="date"
                  value={newItem.garanzia_scadenza}
                  onChange={(e) => setNewItem({ ...newItem, garanzia_scadenza: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label>Documento Acquisto</Label>
              <Input
                value={newItem.documento_acquisto}
                onChange={(e) => setNewItem({ ...newItem, documento_acquisto: e.target.value })}
                placeholder="Fattura n. 123/2026"
              />
            </div>
            
            <div>
              <Label>Note</Label>
              <Textarea
                value={newItem.note}
                onChange={(e) => setNewItem({ ...newItem, note: e.target.value })}
                placeholder="Note aggiuntive..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleCreateItem} disabled={saving} data-testid="save-inventory-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Registra Ingresso
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Dettaglio Articolo</DialogTitle>
          </DialogHeader>
          
          {selectedItem && (
            <div className="space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-lg">{selectedItem.brand} {selectedItem.model}</h3>
                  <p className="text-muted-foreground">{selectedItem.type_code}</p>
                </div>
                <StatusBadge status={selectedItem.stato} />
              </div>
              
              <Separator />
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Seriale</p>
                  <p className="font-mono">{selectedItem.serial_number}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Lotto</p>
                  <p>{selectedItem.lotto || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Magazzino</p>
                  <p>{selectedItem.warehouse?.nome || selectedItem.warehouse_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Ubicazione</p>
                  <p>{selectedItem.ubicazione || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Data Ingresso</p>
                  <p>{selectedItem.data_ingresso || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Garanzia fino a</p>
                  <p>{selectedItem.garanzia_scadenza || '-'}</p>
                </div>
                {selectedItem.contract_number && (
                  <>
                    <div>
                      <p className="text-muted-foreground">Contratto</p>
                      <p className="font-medium">{selectedItem.contract_number}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Data Installazione</p>
                      <p>{selectedItem.data_installazione || '-'}</p>
                    </div>
                  </>
                )}
              </div>
              
              {/* Recent movements */}
              {selectedItem.recent_movements && selectedItem.recent_movements.length > 0 && (
                <>
                  <Separator />
                  <div>
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <History className="h-4 w-4" />
                      Ultimi Movimenti
                    </h4>
                    <ScrollArea className="h-40">
                      {selectedItem.recent_movements.map((mov) => (
                        <div key={mov.id || `mov-${mov.data_movimento}-${mov.movimento_tipo}`} className="flex items-start gap-3 py-2 border-b last:border-0">
                          <div className="text-xs text-muted-foreground w-24">
                            {new Date(mov.data_movimento).toLocaleDateString('it-IT')}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium capitalize">{mov.movimento_tipo.replace('_', ' ')}</p>
                            {mov.note && <p className="text-xs text-muted-foreground">{mov.note}</p>}
                          </div>
                        </div>
                      ))}
                    </ScrollArea>
                  </div>
                </>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Dialog */}
      <TransferDialog
        open={showTransferDialog}
        onOpenChange={setShowTransferDialog}
        item={selectedItem}
        warehouses={warehouses.filter(w => w.attivo)}
        onTransfer={handleTransfer}
        saving={saving}
      />

      {/* Status Change Dialog */}
      <StatusChangeDialog
        open={showStatusDialog}
        onOpenChange={setShowStatusDialog}
        item={selectedItem}
        onChangeStatus={handleChangeStatus}
        saving={saving}
      />

      {/* Warehouse Dialog */}
      <Dialog open={showWarehouseDialog} onOpenChange={setShowWarehouseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedWarehouseItem ? 'Modifica Magazzino' : 'Nuovo Magazzino'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Codice Magazzino *</Label>
                <Input
                  value={warehouseForm.codice_magazzino}
                  onChange={(e) => setWarehouseForm({ ...warehouseForm, codice_magazzino: e.target.value.toUpperCase() })}
                  placeholder="MAG-001"
                  disabled={!!selectedWarehouseItem}
                />
              </div>
              <div>
                <Label>Tipo *</Label>
                <Select 
                  value={warehouseForm.tipo_magazzino}
                  onValueChange={(v) => setWarehouseForm({ ...warehouseForm, tipo_magazzino: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(WAREHOUSE_TYPE_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Nome Magazzino *</Label>
              <Input
                value={warehouseForm.nome_magazzino}
                onChange={(e) => setWarehouseForm({ ...warehouseForm, nome_magazzino: e.target.value })}
                placeholder="Magazzino Centrale Milano"
              />
            </div>
            
            <div>
              <Label>Indirizzo</Label>
              <Input
                value={warehouseForm.indirizzo}
                onChange={(e) => setWarehouseForm({ ...warehouseForm, indirizzo: e.target.value })}
                placeholder="Via Roma, 1 - 20100 Milano"
              />
            </div>
            
            <div>
              <Label>Responsabile</Label>
              <Input
                value={warehouseForm.responsabile}
                onChange={(e) => setWarehouseForm({ ...warehouseForm, responsabile: e.target.value })}
                placeholder="Nome responsabile"
              />
            </div>
            
            <div>
              <Label>Note</Label>
              <Textarea
                value={warehouseForm.note}
                onChange={(e) => setWarehouseForm({ ...warehouseForm, note: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWarehouseDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleSaveWarehouse} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Warehouse Dialog */}
      <AlertDialog open={showDeleteWarehouseDialog} onOpenChange={setShowDeleteWarehouseDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disattiva Magazzino</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler disattivare il magazzino "{selectedWarehouseItem?.nome_magazzino}"?
              {selectedWarehouseItem?.item_count > 0 && (
                <span className="block mt-2 text-red-600 font-medium">
                  Attenzione: questo magazzino contiene {selectedWarehouseItem.item_count} articoli.
                  Non potrà essere disattivato finché contiene articoli.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteWarehouse}
              disabled={saving || (selectedWarehouseItem?.item_count > 0)}
              className="bg-red-600 hover:bg-red-700"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Disattiva
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Transfer Dialog Component
function TransferDialog({ open, onOpenChange, item, warehouses, onTransfer, saving }) {
  const [newWarehouse, setNewWarehouse] = useState('');
  const [ubicazione, setUbicazione] = useState('');
  const [scaffale, setScaffale] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (open) {
      setNewWarehouse('');
      setUbicazione('');
      setScaffale('');
      setNote('');
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Trasferisci Articolo</DialogTitle>
          <DialogDescription>
            Sposta l'articolo in un altro magazzino
          </DialogDescription>
        </DialogHeader>
        
        {item && (
          <div className="space-y-4">
            <div className="bg-muted/50 p-3 rounded-lg">
              <p className="font-medium">{item.brand} {item.model}</p>
              <p className="text-sm text-muted-foreground font-mono">{item.serial_number}</p>
              <p className="text-xs text-muted-foreground mt-1">
                Attualmente in: {item.warehouse_name}
              </p>
            </div>
            
            <div>
              <Label>Nuovo Magazzino *</Label>
              <Select value={newWarehouse} onValueChange={setNewWarehouse}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona destinazione" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.filter(w => w.id !== item.warehouse_id).map(wh => (
                    <SelectItem key={wh.id} value={wh.id}>
                      {wh.nome_magazzino}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Ubicazione</Label>
                <Input
                  value={ubicazione}
                  onChange={(e) => setUbicazione(e.target.value)}
                  placeholder="Scaffale/Area"
                />
              </div>
              <div>
                <Label>Scaffale</Label>
                <Input
                  value={scaffale}
                  onChange={(e) => setScaffale(e.target.value)}
                  placeholder="A1, B2..."
                />
              </div>
            </div>
            
            <div>
              <Label>Note</Label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Motivo trasferimento..."
                rows={2}
              />
            </div>
          </div>
        )}
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button 
            onClick={() => onTransfer(newWarehouse, ubicazione, scaffale, note)}
            disabled={!newWarehouse || saving}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ArrowRightLeft className="h-4 w-4 mr-2" />}
            Trasferisci
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Status Change Dialog Component
function StatusChangeDialog({ open, onOpenChange, item, onChangeStatus, saving }) {
  const [newStatus, setNewStatus] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (open && item) {
      setNewStatus(item.stato);
      setNote('');
    }
  }, [open, item]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cambia Stato</DialogTitle>
          <DialogDescription>
            Modifica lo stato dell'articolo
          </DialogDescription>
        </DialogHeader>
        
        {item && (
          <div className="space-y-4">
            <div className="bg-muted/50 p-3 rounded-lg">
              <p className="font-medium">{item.brand} {item.model}</p>
              <p className="text-sm text-muted-foreground font-mono">{item.serial_number}</p>
            </div>
            
            <div>
              <Label>Nuovo Stato</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      {config.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Note</Label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Motivo del cambio stato..."
                rows={2}
              />
            </div>
          </div>
        )}
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button 
            onClick={() => onChangeStatus(newStatus, note)}
            disabled={!newStatus || newStatus === item?.stato || saving}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Aggiorna Stato
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
